import { useParams, useHistory, useLocation } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import {
  FC,
  ReactNode,
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
  useCallback,
} from 'react';

import { State } from 'redux/initialState';
import { useGetActualSociety } from 'hooks/useGetActualSociety';
import { useGetMenu } from 'modules/administratives/hooks/useGetMenu';
import menuTypes from 'constants/menuTypes';
import { store } from 'redux/store';
import {
  IncentiveOffer,
  IncentiveOfferOperation,
  IncentivesMarketBeneficiary,
} from 'modules/incentivesMarket/types';

import { updateSocietyConfiguration } from 'modules/society/redux/societyActions';
import { getBeneficiaries, toggleBeneficiaryIncentivesMarketAccess } from 'modules/incentivesMarket/api/beneficiaryActions';
import { SocietyEquityMarketStatus } from 'types/Society';
import { IncentivesMarketAdminPanelContextType } from '../types/IncentivesMarketAdminPanelContextType';
import { incentivesMarketAdminPanelInitValue } from '../constants/IncentivesMarketAdminPanelInitValue';
import {
  TRADING_OFFERS_PAGE_SIZE,
  TRADING_OFFERS_FILTERS,
} from '../../../constants/incentiveOffersConfig';
import { useGetIncentiveOffers } from '../../../hooks/useGetIncentiveOffers';
import {
  getIncentiveOfferOperations,
  updateIncentiveOfferOperation,
} from '../../../redux/incentiveOfferOperationActions';
import calculateOffersSummary from '../../../utils/calculateOffersSummary';
import {
  filterSellOffers,
  filterBuyOffers,
} from '../../../utils/filterOffersByType';

export const IncentivesMarketAdminPanelContext =
  createContext<IncentivesMarketAdminPanelContextType>(
    incentivesMarketAdminPanelInitValue
  );

export const IncentivesMarketAdminPanelProvider: FC<{
  children: ReactNode;
}> = ({ children }) => {
  const { societyId } = useParams<{ societyId: string }>();
  const history = useHistory();
  const dispatch = useDispatch();
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const beneficiaryId = queryParams.get('beneficiaryId');
  const beneficiaryQuery = beneficiaryId ? `?beneficiaryId=${beneficiaryId}` : '';

  const actualSociety = useSelector(
    ({ society }: State) => society?.actualSociety
  );
  const isAdmin = useSelector(
    (state: State) => state.society?.role?.isAdmin ?? false
  );
  const incentiveOffers = useSelector(
    ({ incentiveOffers }: State) => (incentiveOffers ?? []) as IncentiveOffer[]
  );
  const [beneficiariesList, setBeneficiariesList] = useState<IncentivesMarketBeneficiary[]>([]);

  const [operations, setOperations] = useState<IncentiveOfferOperation[]>([]);
  const [isOperationsLoading, setIsOperationsLoading] = useState(true);

  useGetActualSociety(societyId);
  useGetMenu(menuTypes.NULL);

  const { isLoading: isIncentiveOffersLoading } = useGetIncentiveOffers({
    societyId: actualSociety ? societyId : undefined,
    page: 1,
    rowsPerPage: TRADING_OFFERS_PAGE_SIZE,
    filters: TRADING_OFFERS_FILTERS,
  });

  useEffect(() => {
    if (!actualSociety || !societyId) return;

    const loadBeneficiaries = async () => {
      try {
        const result: any = await store.dispatch(
          getBeneficiaries(societyId, { items: 500 })
        );
        setBeneficiariesList(result?.data || []);
      } catch {
        setBeneficiariesList([]);
      }
    };

    loadBeneficiaries();
  }, [actualSociety, societyId]);

  useEffect(() => {
    if (!actualSociety || !societyId) return;

    const controller = new AbortController();

    const fetchOperations = async () => {
      try {
        setIsOperationsLoading(true);
        const result = await store.dispatch(
          getIncentiveOfferOperations({ societyId })
        );
        if (!controller.signal.aborted && result) {
          setOperations(result.data || []);
        }
      } finally {
        if (!controller.signal.aborted) {
          setIsOperationsLoading(false);
        }
      }
    };

    fetchOperations();

    return () => controller.abort();
  }, [actualSociety, societyId]);

  const sellOffers = useMemo(
    () => filterSellOffers(incentiveOffers),
    [incentiveOffers]
  );

  const buyOffers = useMemo(
    () => filterBuyOffers(incentiveOffers),
    [incentiveOffers]
  );

  const sellOffersSummary = useMemo(
    () => calculateOffersSummary(sellOffers),
    [sellOffers]
  );

  const buyOffersSummary = useMemo(
    () => calculateOffersSummary(buyOffers),
    [buyOffers]
  );

  const incentivesMarketStatus: SocietyEquityMarketStatus =
    actualSociety?.configuration?.incentivesMarket?.status ||
    SocietyEquityMarketStatus.OPEN;

  const [isStatusChanging, setIsStatusChanging] = useState(false);

  const handleIncentivesMarketStatusChange = useCallback(
    async (status: SocietyEquityMarketStatus) => {
      if (!societyId) return;
      try {
        setIsStatusChanging(true);
        await dispatch(
          updateSocietyConfiguration(
            societyId,
            { 'incentivesMarket.status': status },
            true
          )
        );
      } finally {
        setIsStatusChanging(false);
      }
    },
    [dispatch, societyId]
  );

  const pendingOperationsCount = useMemo(
    () =>
      operations.filter(
        (op) =>
          op.status !== 'COMPLETED' &&
          op.status !== 'CANCELLED' &&
          op.status !== 'REJECTED'
      ).length,
    [operations]
  );

  const handleGoBack = useCallback(() => {
    history.push(`/mercado-de-incentivos/${societyId}${beneficiaryQuery}`);
  }, [history, societyId, beneficiaryQuery]);

  const handleViewOfferDetail = useCallback(
    (offerId: string) => {
      history.push(`/detalle-oferta-incentivos/${societyId}/${offerId}${beneficiaryQuery}`);
    },
    [history, societyId, beneficiaryQuery]
  );

  const handleViewOperationDetail = useCallback(
    (operationId: string) => {
      history.push(
        `/detalle-operacion-incentivos/${societyId}/${operationId}${beneficiaryQuery}`
      );
    },
    [history, societyId, beneficiaryQuery]
  );

  const handleUpdateOperationStatus = useCallback(
    async (operationId: string, status: string, comment?: string) => {
      await dispatch(
        updateIncentiveOfferOperation(operationId, { status, comment } as any)
      );
      const result = await store.dispatch(
        getIncentiveOfferOperations({ societyId })
      );
      if (result) {
        setOperations(result.data || []);
      }
    },
    [dispatch, societyId]
  );

  const handleTogglePartnerIncentivesMarketAccess = useCallback(
    async (beneficiaryIds: string[], enabled: boolean) => {
      await dispatch(toggleBeneficiaryIncentivesMarketAccess({ beneficiaryIds, enabled }));
    },
    [dispatch]
  );

  const contextValue = useMemo(
    () => ({
      data: {
        societyId: societyId || '',
        societyName: actualSociety?.name || '',
        incentivesMarketStatus,
        sellOffers,
        buyOffers,
        operations,
        sellOffersSummary,
        buyOffersSummary,
        pendingOperationsCount,
        partners: beneficiariesList,
        isAdmin,
        isLoading:
          isIncentiveOffersLoading ||
          isOperationsLoading ||
          isStatusChanging ||
          !actualSociety,
      },
      actions: {
        handleGoBack,
        handleIncentivesMarketStatusChange,
        handleViewOfferDetail,
        handleViewOperationDetail,
        handleUpdateOperationStatus,
        handleTogglePartnerIncentivesMarketAccess,
      },
    }),
    [
      societyId,
      actualSociety,
      sellOffers,
      buyOffers,
      operations,
      sellOffersSummary,
      buyOffersSummary,
      pendingOperationsCount,
      beneficiariesList,
      isAdmin,
      isIncentiveOffersLoading,
      isOperationsLoading,
      isStatusChanging,
      incentivesMarketStatus,
      handleGoBack,
      handleIncentivesMarketStatusChange,
      handleViewOfferDetail,
      handleViewOperationDetail,
      handleUpdateOperationStatus,
      handleTogglePartnerIncentivesMarketAccess,
    ]
  );

  return (
    <IncentivesMarketAdminPanelContext.Provider value={contextValue}>
      {children}
    </IncentivesMarketAdminPanelContext.Provider>
  );
};

export const useIncentivesMarketAdminPanel = () => {
  const context = useContext(IncentivesMarketAdminPanelContext);
  if (!context) {
    throw new Error(
      'useIncentivesMarketAdminPanel must be used within an IncentivesMarketAdminPanelProvider'
    );
  }
  return context;
};
